import {
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TableHead,
  Paper,
  LinearProgress,
} from "@mui/material";

import { DEPOSITS_GROUP_BY_OPTIONS } from "../utils/consts";
import { Grouping, RawData, Deposit, AggregatedDepositData } from "../types";
import { buildGroupTree, capitaliseFirstLetter } from "../utils/utils";
import { renderGroupRowsWithRowSpan } from "./utils";
import { ReportTypes } from "../ReportsDashboard";

interface DepositsTableProps {
  groupings: Grouping[];
  isLoading: boolean;
  isFetching: boolean;
  rawData?: RawData<Deposit>;
  aggregatedDepositData?: AggregatedDepositData[];
}

const DepositsTable = ({
  groupings,
  isLoading,
  isFetching,
  rawData,
  aggregatedDepositData,
}: DepositsTableProps) => {
  const aggregatedColumns = ["Number of Deposits", "Amount (USD)"];

  const rawColumns = [
    "Deposit ID",
    "Client ID",
    "Deposit Status",
    "PSP",
    "PSP Transaction ID",
    "Currency Type",
    "Amount (USD)",
    "Local Amount",
    "Local Currency",
    "Exchange Rate",
    "Created At",
  ];

  const columns = groupings.length > 0 ? aggregatedColumns : rawColumns;

  const cellSx = {
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    overflow: "hidden",
  };
  const rawRows =
    isLoading || isFetching
      ? []
      : rawData?.map((deposit, idx) => {
          return (
            <TableRow key={idx}>
              <TableCell align="right" sx={cellSx}>
                {deposit.depositId.toLocaleString()}
              </TableCell>
              <TableCell align="right" sx={cellSx}>
                {deposit.userId.toLocaleString()}
              </TableCell>
              <TableCell align="right" sx={cellSx}>
                {capitaliseFirstLetter(
                  deposit.transactionStatus.toLocaleString().toLowerCase()
                )}
              </TableCell>
              <TableCell align="right" sx={cellSx}>
                {(deposit.psp || "Unknown PSP").toLocaleString()}
              </TableCell>
              <TableCell align="right" sx={cellSx}>
                {deposit.pspTransactionId || "-"}
              </TableCell>
              <TableCell align="right" sx={cellSx}>
                {deposit.currencyType || "-"}
              </TableCell>
              <TableCell align="right" sx={cellSx}>
                {`$${(deposit.amount / 100).toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}`}
              </TableCell>
              <TableCell align="right" sx={cellSx}>
                {`$${((deposit.localAmount || 0) / 100).toLocaleString(
                  undefined,
                  {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }
                )}`}
              </TableCell>
              <TableCell align="right" sx={cellSx}>
                {deposit.localCurrency || "-"}
              </TableCell>
              <TableCell align="right" sx={cellSx}>
                {`${
                  deposit.exchangeRate === null
                    ? "-"
                    : deposit.exchangeRate.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })
                }`}
              </TableCell>
              <TableCell align="right" sx={cellSx}>
                {new Date(deposit.createdAt).toLocaleString()}
              </TableCell>
            </TableRow>
          );
        });

  return (
    <TableContainer
      component={Paper}
      sx={{ mt: 2 }}
      style={{
        border: "1px solid rgba(255, 255, 255, 0.1)",
        boxShadow: "none",
      }}
    >
      <MuiTable>
        <TableHead>
          <TableRow>
            {groupings.map((g, idx) => (
              <TableCell
                key={idx}
                sx={{
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                  textTransform: "none",
                }}
              >
                <b style={{ textTransform: "none" }}>
                  {DEPOSITS_GROUP_BY_OPTIONS.find(
                    (opt) => opt.value === g.groupBy
                  )?.name || g.groupBy}
                </b>
              </TableCell>
            ))}
            {columns.map((col, idx) => (
              <TableCell
                sx={{
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                  textTransform: "none",
                }}
                key={idx}
              >
                <b style={{ textTransform: "none" }}>{col}</b>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {isLoading || isFetching || (!aggregatedDepositData && !rawData) ? (
            <TableRow sx={{ padding: 0 }}>
              <TableCell colSpan={groupings.length + 6} sx={{ padding: 0 }}>
                <LinearProgress />
              </TableCell>
            </TableRow>
          ) : null}
          {aggregatedDepositData &&
            groupings.length > 0 &&
            renderGroupRowsWithRowSpan(
              buildGroupTree(
                aggregatedDepositData.filter((row) => {
                  // Split the groupKey by ' + ' to get each grouping's value
                  const keyParts = row.groupKey.split(" + ");
                  return groupings.every((g, idx) => {
                    const filter: string[] = g.groupFilter;
                    if (!filter || filter.length === 0) return true;
                    return filter.includes(keyParts[idx]);
                  });
                }),
                groupings
              ),
              groupings,
              undefined,
              ReportTypes.Deposits
            )}
          {rawData && groupings.length === 0 && rawRows}
        </TableBody>
      </MuiTable>
    </TableContainer>
  );
};

export default DepositsTable;
